import { useState, useRef, useEffect, useCallback } from 'react';
import { agentFetch } from '../pages/agent/AgentLayout';

/**
 * useResumableChat — état + logique du panneau de chat assistant, conçu pour
 * VIVRE DANS LE COMPOSANT PARENT (AgentSession) afin de survivre aux
 * changements de phase (résultats ↔ « affiner la recherche »).
 *
 * C'est le correctif racine de la classe « remount » remontée par le panel :
 * comme l'effet de reprise est keyé sur `clientId` et que le hook n'est pas
 * démonté lors d'un aller-retour intra-appel, il n'y a NI re-fetch, NI perte
 * d'état (messages/saisie), NI bannière de reprise « mensongère » — ce qui
 * rend inutiles les anciens garde-fous (chatLoadedRef / allowResumeBanner /
 * onChatLoaded), désormais supprimés.
 *
 * Contrat de reprise (aligné backend `findLatestByLeadId` + use case) : on ne
 * restaure qu'une session conversationnelle NON vide et NON anonymisée ; les
 * sessions de génération d'email (`kind: 'email_generation'`) sont créées à
 * part via `createEmailSession()` et ne polluent jamais le fil.
 *
 * @param {string} clientId        lead courant (déclenche/relance la reprise).
 * @param {object} client          pour construire le clientContext (option).
 * @param {object} opts
 * @param {(client:object)=>string} opts.buildClientContext  résumé client → prompt.
 * @param {(msg:string)=>void}      opts.onError             toast d'erreur.
 */
export function useResumableChat(clientId, client, opts = {}) {
  const { buildClientContext, onError } = opts;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [resumedAt, setResumedAt] = useState(null);
  const [resumedAgentId, setResumedAgentId] = useState(null);
  const [restoredCount, setRestoredCount] = useState(0);

  // `started` : garde anti-course synchrone (un envoi pendant la reprise ne
  // doit jamais être écrasé par le fetch). `emailSession` : session technique
  // de génération d'email, distincte du fil conversationnel.
  const startedRef = useRef(false);
  const emailSessionRef = useRef(null);
  // Refs DOM exposés à la vue (scroll auto + focus).
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reprise : restaure la dernière conversation NON vide du lead, UNE fois par
  // clientId. (Le hook vivant dans le parent, cet effet ne se rejoue PAS sur un
  // changement de phase → pas de re-fetch ni de bannière fausse intra-appel.)
  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    startedRef.current = false;
    emailSessionRef.current = null;
    setSessionId(null);
    setMessages([]);
    setResumedAt(null);
    setResumedAgentId(null);
    setRestoredCount(0);
    setRestoring(true);
    // Filet : si le fetch ne se résout jamais (connexion qui pend), on débloque
    // l'envoi au bout de 8 s (au pire on perd l'historique, pas l'usage).
    const timeout = setTimeout(() => { if (!cancelled) setRestoring(false); }, 8000);
    agentFetch(`/api/agent/chat/sessions?leadId=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || startedRef.current || !data?.session) return;
        const restored = (data.session.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        }));
        if (!restored.length) return;
        startedRef.current = true;
        setSessionId(data.session.id);
        setMessages(restored);
        setRestoredCount(restored.length);
        const updatedAt = data.session.updatedAt ? new Date(data.session.updatedAt) : null;
        if (updatedAt && !Number.isNaN(updatedAt.getTime())) setResumedAt(updatedAt);
        if (data.session.agentId) setResumedAgentId(data.session.agentId);
      })
      .catch(() => { /* historique indisponible → panneau vide, non bloquant */ })
      .finally(() => { if (!cancelled) { clearTimeout(timeout); setRestoring(false); } });
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [clientId]);

  const send = useCallback(async () => {
    // On attend la fin de la reprise (≈montage) pour ne pas créer une 2e
    // session concurrente de celle restaurée.
    if (!input.trim() || sending || restoring) return;
    const msg = input.trim();
    startedRef.current = true;
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setSending(true);
    try {
      let sid = sessionId;
      if (!sid) {
        const startRes = await agentFetch('/api/agent/chat/sessions', {
          method: 'POST',
          body: JSON.stringify({ leadId: clientId }),
        });
        if (!startRes.ok) throw new Error('chat start failed');
        const session = await startRes.json();
        sid = session.id;
        setSessionId(sid);
      }
      const ctx = buildClientContext ? buildClientContext(client) : '';
      const res = await agentFetch(`/api/agent/chat/sessions/${sid}/messages`, {
        method: 'POST',
        body: JSON.stringify({ userMessage: msg, ...(ctx && { clientContext: ctx }) }),
      });
      if (!res.ok) throw new Error('chat message failed');
      const out = await res.json();
      const reply = out.assistantMessage || out.assistantReply;
      if (reply) setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch {
      onError?.("Claude n'a pas pu répondre. Vérifiez votre connexion et réessayez.");
    } finally {
      setSending(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [input, sending, restoring, sessionId, clientId, client, buildClientContext, onError]);

  // Repartir sur un fil neuf (ancienne conversation hors-sujet). Ne touche pas
  // au reste de la page. `started=true` empêche un re-restore parasite.
  const startNewConversation = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setResumedAt(null);
    setResumedAgentId(null);
    setRestoredCount(0);
    startedRef.current = true;
  }, []);

  // Session DÉDIÉE à la génération d'email (kind=email_generation), réutilisée
  // au sein de l'appel ; jamais restaurée dans le fil conversationnel.
  const createEmailSession = useCallback(async () => {
    if (emailSessionRef.current) return emailSessionRef.current;
    const startRes = await agentFetch('/api/agent/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({ leadId: clientId, kind: 'email_generation' }),
    });
    if (!startRes.ok) throw new Error('chat start failed');
    const session = await startRes.json();
    emailSessionRef.current = session.id;
    return session.id;
  }, [clientId]);

  return {
    messages, input, setInput, sessionId, sending, restoring,
    resumedAt, resumedAgentId, restoredCount,
    send, startNewConversation, createEmailSession,
    bottomRef, inputRef,
  };
}
